<template>
    <div class="stf-radio-container">
        <input
            type="radio"
            :id="id"
            :name="name"
            :value="value"
            :checked="modelValue === value"
            :disabled="disabled"
            class="stf-radio-input"
            @change="updateValue"
        />
        <label :for="id" class="stf-radio-label" :class="{ 'stf-radio-label-disabled': disabled }">
            <span class="stf-radio-button" :class="`stf-radio-button--${size} stf-radio-button--${color}`"></span>
            <span v-if="label.length > 0 || $slots.default" class="stf-radio-text">
                <slot>{{ label }}</slot>
            </span>
        </label>
    </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue';

defineOptions({
    name: 'StfRadioButton'
});

const props = defineProps({
    modelValue: {
        type: [String, Number, Boolean],
        required: true
    },
    value: {
        type: [String, Number, Boolean],
        required: true
    },
    label: {
        type: String,
        default: ''
    },
    name: {
        type: String,
        required: true
    },
    id: {
        type: String,
        default: () => `radio-${Math.random().toString(36).substring(2, 9)}`
    },
    disabled: {
        type: Boolean,
        default: false
    },
    size: {
        type: String,
        default: 'default',
        validator: (value: string) => ['default', 'medium'].includes(value)
    },
    color: {
        type: String,
        default: 'default',
        validator: (value: string) => ['primary', 'default'].includes(value)
    }
});

const emit = defineEmits(['update:modelValue']);

const updateValue = () => {
    emit('update:modelValue', props.value);
};
</script>

<style lang="scss" scoped>
@use '@/assets/sass/_variables' as vars;
.stf-radio-container {
  display: flex;
  align-items: center;
}

.stf-radio-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;

  &:checked + .stf-radio-label .stf-radio-button {
    border-color: vars.$color-link;
    border-width: 3px;

    &.stf-radio-button--primary {
      border-color: vars.$color-primary;
    }
  }

  &:checked + .stf-radio-label .stf-radio-button--medium {
    border-width: 5px;
  }

  &:disabled + .stf-radio-label {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.stf-radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: vars.$font-size-16;

  &.stf-radio-label-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.stf-radio-button {
  position: relative;
  display: inline-block;
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid #6b7280;
  background-color: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s ease-in-out;

  &.stf-radio-button--medium {
    width: 16px;
    height: 16px;
    border-width: 2px;
  }
}

.stf-radio-text {
  line-height: 1.3;
  margin-left: 10px;
}
</style>
